import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useSettings } from "@/contexts/SettingsContext";
import { TrendingUp, ArrowUp, ArrowDown, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

export default function SharedTrade() {
  const { t } = useI18n();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const [trade, setTrade] = useState(null);
  const [err, setErr] = useState("");
  const { settings, loading: settingsLoading } = useSettings();

  useEffect(() => {
    api.get(`/share/trade/${id}`).then(({ data }) => setTrade(data)).catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [id]);

  const registerHref = ref ? `/register?ref=${ref}` : "/register";

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 py-12 radial-glow">
        {err && <div className="text-sell text-center py-16">{err}</div>}
        {!err && !trade && <div className="text-dim text-center py-16 font-mono">{t("share_loading")}</div>}
        {trade && (
          <div>
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-2">{t("share_title")}</div>
              <div className="text-lg text-muted">
                <span className="text-white font-semibold">{trade.trader_name}</span> just closed a {trade.type.toUpperCase()} trade on
                <span className="text-buy"> {trade.asset_name}</span>
              </div>
            </div>

            <div data-testid="shared-card" className="relative overflow-hidden bg-panel border border-line rounded-md p-8">
              <div className="absolute inset-0" style={{
                backgroundImage: trade.won
                  ? "radial-gradient(700px 350px at 80% 20%, rgba(0,230,118,0.18), transparent 60%)"
                  : "radial-gradient(700px 350px at 80% 20%, rgba(255,59,48,0.16), transparent 60%)",
              }} />
              <div className="relative flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  {settingsLoading ? (
                    <div className="w-9 h-9 bg-transparent"></div>
                  ) : settings?.logo_base64 ? (
                    <img src={settings.logo_base64} alt="Logo" className="max-h-9" />
                  ) : (
                    <>
                      <div className="w-9 h-9 bg-buy flex items-center justify-center rounded-sm"><TrendingUp className="w-5 h-5 text-black" /></div>
                      <span className="text-xl font-bold tracking-tight">{settings?.site_title || <>IQ<span className="text-buy">Trade</span></>}</span>
                    </>
                  )}
                </div>
                <span className={`text-xs uppercase tracking-widest px-3 py-1 rounded-sm border ${trade.won ? "border-buy text-buy" : "border-sell text-sell"}`}>
                  {trade.won ? "WIN" : "LOSS"}
                </span>
              </div>
              <div className="relative">
                <div className="text-xs text-dim uppercase tracking-widest">{trade.type} · {trade.asset_name}</div>
                <div className="flex items-baseline gap-4 mt-2">
                  <div className={`text-6xl font-extrabold font-mono ${trade.won ? "text-buy" : "text-sell"}`}>
                    {trade.pnl >= 0 ? "+" : ""}${Math.abs(trade.pnl).toFixed(2)}
                  </div>
                  <div className={`text-xl font-mono ${trade.won ? "text-buy" : "text-sell"}`}>
                    {trade.roi_pct >= 0 ? "+" : ""}{trade.roi_pct.toFixed(2)}%
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm font-mono">
                  <Meta k="Direction" v={<span className={`inline-flex items-center gap-1 ${trade.direction === "call" || trade.direction === "buy" ? "text-buy" : "text-sell"}`}>{trade.direction === "call" || trade.direction === "buy" ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}{trade.direction.toUpperCase()}</span>} />
                  <Meta k="Amount" v={`$${Number(trade.amount).toFixed(2)}${trade.type === "cfd" ? ` · ${trade.leverage}x` : ""}`} />
                  <Meta k="Entry" v={Number(trade.entry_price).toFixed(2)} />
                  <Meta k="Close" v={trade.close_price ? Number(trade.close_price).toFixed(2) : "—"} />
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 text-center bg-panel border border-line rounded-sm p-6">
              <div className="text-xl font-bold mb-2">{t("share_cta_title")}</div>
              <div className="text-sm text-muted mb-4">{t("share_cta_desc")}</div>
              <Link to={registerHref} data-testid="shared-cta-register" className="inline-flex items-center gap-2 bg-buy text-black font-bold px-6 py-3 rounded-sm hover:bg-emerald-400">
                Claim your account <ArrowRight className="w-4 h-4"/>
              </Link>
              {ref && <div className="text-[10px] uppercase tracking-widest text-dim mt-3">Referred by <span className="text-buy font-mono">{ref}</span></div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Meta = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-dim">{k}</div>
    <div className="mt-1">{v}</div>
  </div>
);
